import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../../utils/api';
import { RefreshCw, Check, X, Clock } from 'lucide-react';

interface ReadinessCheck {
  name: string;
  status: 'passed' | 'failed' | 'pending' | 'not_required';
  required: boolean;
  message: string;
  details?: Record<string, any>;
}

interface BlockingFlag {
  id: string;
  title: string;
  status: string;
  source?: unknown;
}

// GET /api/v1/case-signoff/{id}/readiness — single source of truth shared with the
// payload's sign_off_blockers and the POST gate (case_flag_service.assess_signoff_blockers).
interface ReadinessResult {
  ready: boolean;
  blockers: string[];
  blocking_flags: BlockingFlag[];
  checks: ReadinessCheck[];
  readiness_blocking_issues: string[];
}

interface SignOffReadinessMergedCardProps {
  caseId: string;
  signOffReady: boolean;
  signOffBlockers: string[];
  onSignedOff?: () => void;
}

type RowStatus = 'passed' | 'failed' | 'missing' | 'pending' | 'not_required';

interface CheckRow {
  id: string;
  label: string;
  message: string;
  status: RowStatus;
  required: boolean;
  isSynthesized?: boolean;
  isFlag?: boolean;
  isGenericBlocker?: boolean;
}

export default function SignOffReadinessMergedCard({
  caseId,
  signOffReady,
  signOffBlockers,
  onSignedOff,
}: SignOffReadinessMergedCardProps) {
  const navigate = useNavigate();

  // Destination helper for Resolve links
  const getResolveDestination = (row: CheckRow): string => {
    // Blocker-flag rows → the case path flags inbox (where they are disposed)
    if (row.isFlag) return `/case/${caseId}/path`;

    // Missing-doc rows (status 'missing' / isSynthesized) → the upload wizard
    if (row.status === 'missing' || row.isSynthesized) {
      return `/wizard-v2?claim=${caseId}&step=3&section=upload`;
    }

    const labelLower = row.label.toLowerCase();
    const messageLower = row.message.toLowerCase();
    const combined = `${labelLower} ${messageLower}`;

    // Reconciliation rows
    if (
      combined.includes('p&l') ||
      combined.includes('reconcil') ||
      combined.includes('transaction') ||
      combined.includes('categor')
    ) {
      return `/accountant/reports/${caseId}/reconcile`;
    }

    // Document rows
    if (
      labelLower.includes('bank') ||
      labelLower.includes('tax') ||
      labelLower.includes('k-1') ||
      labelLower.includes('k1') ||
      labelLower.includes('statement') ||
      labelLower.includes('return') ||
      labelLower.includes('document')
    ) {
      return `/accountant/reports/${caseId}/intake`;
    }

    // Clarifications
    if (labelLower.includes('clarification')) {
      return `/accountant/reports/${caseId}/intake`;
    }

    // Fallback for any other needs-work row
    return `/accountant/reports/${caseId}/intake`;
  };

  const [readiness, setReadiness] = useState<ReadinessResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ message: string; status?: number } | null>(null);

  // Sign-off CTA state (preserved from SignOffReadinessCard)
  const [armed, setArmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [postError, setPostError] = useState<string | null>(null);
  const [postFailureBlockers, setPostFailureBlockers] = useState<string[] | null>(null);
  const [doneAt, setDoneAt] = useState<string | null>(null);

  // Request-sequence guard: ignore stale in-flight responses when caseId
  // changes rapidly (tab switches, fast navigation).
  const requestSeq = useRef(0);

  useEffect(() => {
    fetchReadiness();
  }, [caseId]);

  useEffect(() => {
    // Reset sign-off state when caseId changes
    setArmed(false);
    setSubmitting(false);
    setPostError(null);
    setPostFailureBlockers(null);
    setDoneAt(null);
  }, [caseId]);

  const fetchReadiness = async () => {
    if (!caseId) return;
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<ReadinessResult>(
        `/api/v1/case-signoff/${caseId}/readiness`,
        { timeoutMs: 30000 }
      );
      if (seq !== requestSeq.current) return; // stale
      setReadiness(data);
    } catch (err: any) {
      if (seq !== requestSeq.current) return; // stale
      console.error('Error fetching readiness:', err);
      const status = typeof err.status === 'number' ? err.status : undefined;
      setError({
        message: status ? `Error ${status}` : 'Request failed',
        status,
      });
    } finally {
      if (seq === requestSeq.current) {
        setLoading(false);
      }
    }
  };

  // Server readiness is authoritative; the payload prop is only a fallback until it loads.
  const effectiveBlockers = readiness ? readiness.blockers : signOffBlockers;
  const effectiveReady = readiness ? readiness.ready : signOffReady;

  // Build rows from checks + synthesized missing doc rows + generic blocker rows
  const rows: CheckRow[] = React.useMemo(() => {
    const result: CheckRow[] = [];
    const consumedBlockers = new Set<number>();

    // 1. Add checks from API
    if (readiness?.checks) {
      readiness.checks.forEach((check, idx) => {
        result.push({
          id: `check-${idx}`,
          label: check.name + (check.required ? '' : ' (optional)'),
          message: check.message,
          status: check.status,
          required: check.required,
        });
      });
    }

    // Helper to find a check row by its canonical name (case-insensitive, ignoring " (optional)")
    const findCheckIndex = (name: string) =>
      result.findIndex(
        (r) =>
          r.id.startsWith('check-') &&
          r.label.toLowerCase().replace(' (optional)', '').trim() === name.toLowerCase()
      );

    // 2. Override Transaction Review / Clarifications check rows when a blocker
    //    clearly belongs to that category, even if the readiness check is passed.
    effectiveBlockers.forEach((blocker, bIdx) => {
      if (/transaction\(s\) need review/i.test(blocker)) {
        const idx = findCheckIndex('Transaction Review');
        if (idx !== -1) {
          result[idx].status = 'failed';
          result[idx].message = blocker;
          consumedBlockers.add(bIdx);
        }
      } else if (/clarification\(s\) pending/i.test(blocker)) {
        const idx = findCheckIndex('Clarifications');
        if (idx !== -1) {
          result[idx].status = 'failed';
          result[idx].message = blocker;
          consumedBlockers.add(bIdx);
        }
      }
    });

    // 3. One row per blocking flag (open or acknowledged) — same set the POST gate enforces
    (readiness?.blocking_flags ?? []).forEach((flag) => {
      result.push({
        id: `flag-${flag.id}`,
        label: 'Consistency-gate blocker',
        message: `${flag.title} — ${flag.status}`,
        status: 'failed',
        required: true,
        isFlag: true,
      });
    });

    // Consume blocker strings that correspond to flags
    effectiveBlockers.forEach((blocker, bIdx) => {
      if (/^blocking flag/i.test(blocker)) {
        consumedBlockers.add(bIdx);
      }
    });

    // 4. Add synthesized rows for missing documents from blockers
    effectiveBlockers.forEach((blocker, bIdx) => {
      const match = blocker.match(/^Missing (?:required documents?: )?(.+)$/i);
      if (match) {
        const docName = match[1];
        // Avoid duplicates if the API already listed this as failed or missing
        const exists = result.some(
          (r) =>
            r.label.toLowerCase().replace(' (optional)', '').trim() === docName.toLowerCase() &&
            (r.status === 'failed' || r.status === 'missing')
        );
        if (exists) {
          consumedBlockers.add(bIdx);
        } else {
          result.push({
            id: `missing-${bIdx}`,
            label: docName,
            message: 'Required · not on file',
            status: 'missing',
            required: true,
            isSynthesized: true,
          });
          consumedBlockers.add(bIdx);
        }
      }
    });

    // Helper: a blocker string is "represented" if any existing non-passing row
    // already shows the same message (trimmed, case-insensitive).
    const isBlockerRepresented = (blocker: string) =>
      result.some(
        (r) =>
          r.status !== 'passed' &&
          r.status !== 'not_required' &&
          r.message.trim().toLowerCase() === blocker.trim().toLowerCase()
      );

    // 5. Generic rows for any remaining blocker not already represented
    effectiveBlockers.forEach((blocker, bIdx) => {
      if (!consumedBlockers.has(bIdx)) {
        if (isBlockerRepresented(blocker)) {
          consumedBlockers.add(bIdx);
          return;
        }
        result.push({
          id: `blocker-${bIdx}`,
          label: 'Sign-off blocker',
          message: blocker,
          status: 'failed',
          required: true,
          isGenericBlocker: true,
        });
      }
    });

    return result;
  }, [readiness, effectiveBlockers]);

  // Compute counts: only readiness check rows count toward "N of M required checks passed"
  const checkRows = rows.filter((r) => !r.isFlag && !r.isSynthesized && !r.isGenericBlocker);
  const totalRequired = checkRows.filter((r) => r.required).length;
  const passedRequired = checkRows.filter((r) => r.required && r.status === 'passed').length;
  const blockerCount = rows.filter((r) => r.required && (r.status === 'failed' || r.status === 'missing')).length;
  const isReady = effectiveReady && postFailureBlockers === null && blockerCount === 0;

  // Status pill for checklist rows
  const StatusPill = ({ status, label }: { status: RowStatus; label: string }) => {
    const isBlocked = status === 'failed' || status === 'missing';
    const isPassed = status === 'passed';

    let icon = <Clock className="w-3 h-3" />;
    if (isPassed) icon = <Check className="w-3 h-3" />;
    if (isBlocked) icon = <X className="w-3 h-3" />;

    const ringColor = isBlocked ? 'var(--color-blocker)' : 'var(--color-border)';
    const textColor = isBlocked ? 'var(--color-blocker)' : 'var(--color-text-secondary)';

    return (
      <span
        className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold"
        style={{
          background: 'transparent',
          boxShadow: `inset 0 0 0 1px ${ringColor}`,
          color: textColor,
        }}
      >
        {icon}
        {label}
      </span>
    );
  };

  // Header status pill
  const HeaderStatusPill = () => {
    if (isReady) {
      return (
        <span
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold"
          style={{
            background: 'transparent',
            boxShadow: 'inset 0 0 0 1px var(--color-border)',
            color: 'var(--color-text-secondary)',
          }}
        >
          <Check className="w-3 h-3" />
          Ready
        </span>
      );
    }
    return (
      <span
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold"
        style={{
          background: 'transparent',
          boxShadow: 'inset 0 0 0 1px var(--color-blocker)',
          color: 'var(--color-blocker)',
        }}
      >
        <X className="w-3 h-3" />
        {blockerCount} blocker{blockerCount !== 1 ? 's' : ''}
      </span>
    );
  };

  // Handle sign-off POST (preserved behavior)
  const handleSignOffClick = () => {
    if (submitting) return;
    if (!armed) {
      setArmed(true);
    } else {
      setSubmitting(true);
      setPostError(null);
      api.post(`/api/v1/case-signoff/${caseId}`, {}, { showErrorToast: false })
        .then((res: any) => {
          if (res?.success && (res.action === 'signed_off' || res.action === 'already_signed_off')) {
            setDoneAt(res.signed_off_at || new Date().toISOString());
            setPostFailureBlockers(null);
            onSignedOff?.();
          } else {
            setPostFailureBlockers(Array.isArray(res?.blocking_issues) ? res.blocking_issues : []);
            setPostError(res?.message || 'Sign-off blocked');
            setArmed(false);
          }
        })
        .catch(() => {
          setPostError('Sign-off failed');
          setArmed(false);
        })
        .finally(() => setSubmitting(false));
    }
  };

  // Empty caseId guard — render unavailable card instead of a guaranteed 404.
  if (!caseId) {
    return (
      <div
        className="rounded-[2px] p-6"
        style={{
          backgroundColor: 'var(--color-bg-surface)',
          border: '1px solid var(--color-border)'
        }}
      >
        <div className="flex items-center gap-3 mb-6">
          <h2 className="cs-h2" style={{ color: 'var(--color-text-primary)' }}>Sign-Off Readiness</h2>
        </div>
        <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
          Sign-off readiness unavailable — no case selected.
        </p>
      </div>
    );
  }

  // Signed off state
  if (doneAt) {
    return (
      <div
        className="rounded-[2px] p-6"
        style={{
          backgroundColor: 'var(--color-bg-surface)',
          border: '1px solid var(--color-border)'
        }}
      >
        <div className="flex flex-col items-center justify-center py-8 gap-3">
          <Check className="w-8 h-8" style={{ color: 'var(--color-text-secondary)' }} />
          <div className="text-base font-medium" style={{ color: 'var(--color-text-primary)' }}>
            Signed Off
          </div>
          <div className="font-mono text-xs" style={{ color: 'var(--color-text-dim)' }}>
            {new Date(doneAt).toLocaleString()}
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div
        className="rounded-[2px] p-6"
        style={{
          backgroundColor: 'var(--color-bg-surface)',
          border: '1px solid var(--color-border)'
        }}
      >
        <div className="flex items-center gap-3 mb-6">
          <h2 className="cs-h2" style={{ color: 'var(--color-text-primary)' }}>Sign-Off Readiness</h2>
        </div>
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="w-4 h-4 animate-spin mr-2" style={{ color: 'var(--color-text-secondary)' }} />
          <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Loading readiness checks…</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="rounded-[2px] p-6"
        style={{ backgroundColor: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}
        data-testid="signoff-readiness-error"
      >
        <h2 className="cs-h2 mb-3" style={{ color: 'var(--color-text-primary)' }}>Sign-Off Readiness</h2>
        <p className="text-sm font-medium" style={{ color: 'var(--color-blocker)' }}>
          Could not load sign-off readiness
        </p>
        <p className="mt-1 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
          {error.message}
        </p>
        <button
          onClick={fetchReadiness}
          className="mt-3 text-xs underline"
          style={{ color: 'var(--color-text-secondary)' }}
        >
          Retry
        </button>
      </div>
    );
  }

  const hasMissingDocs = rows.some((r) => r.status === 'missing');

  return (
    <div
      className="rounded-[2px] p-6"
      style={{
        backgroundColor: 'var(--color-bg-surface)',
        border: '1px solid var(--color-border)'
      }}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex-1">
          <h2 className="cs-h2 mb-1.5" style={{ color: 'var(--color-text-primary)' }}>
            Sign-Off Readiness
          </h2>
          <p className="text-[13px] mb-1" style={{ color: 'var(--color-text-secondary)' }} data-testid="signoff-header-count">
            <span className="font-mono">{passedRequired}</span> of <span className="font-mono">{totalRequired}</span> required checks passed — {isReady ? 'ready for sign-off' : `resolve ${blockerCount} blocker${blockerCount !== 1 ? 's' : ''} to enable sign-off`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchReadiness}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3 h-8 rounded-[2px] text-xs font-medium transition-colors hover:opacity-80"
            style={{
              backgroundColor: 'transparent',
              border: '1px solid var(--color-border)',
              color: 'var(--color-text-secondary)'
            }}
            title="Re-run checks"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Re-run checks
          </button>
          <HeaderStatusPill />
        </div>
      </div>

      {/* Section Label */}
      <div className="text-[11px] font-semibold uppercase tracking-[0.1em] mb-3" style={{ color: 'var(--color-text-dim)' }}>
        Readiness Checks
      </div>

      {/* Checklist */}
      <div className="mb-5">
        {rows.length === 0 ? (
          <div className="py-4 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            No readiness checks available.
          </div>
        ) : (
          <div>
            {rows.map((row, idx) => {
              const isLast = idx === rows.length - 1;
              const isFailed = row.status === 'failed' || row.status === 'missing';
              const isOptional = !row.required;
              const needsWork = row.status === 'failed' || row.status === 'missing' || row.status === 'pending';

              let pillLabel = 'Verified';
              if (row.status === 'pending') pillLabel = 'Pending';
              if (row.status === 'not_required') pillLabel = 'Optional';
              if (row.status === 'missing') pillLabel = 'Missing';
              else if (isFailed) pillLabel = 'Blocked';

              // Optional checks stay hueless (gray) even if failed
              const pillStatus = isOptional && isFailed ? 'not_required' : row.status;

              return (
                <div
                  key={row.id}
                  className="flex items-center justify-between py-3.5 min-h-[52px]"
                  style={{
                    borderBottom: isLast ? 'none' : '1px solid var(--color-border)',
                  }}
                >
                  <div className="flex-1 pr-4">
                    <span className="text-sm whitespace-nowrap" style={{ color: 'var(--color-text-primary)' }}>
                      {row.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-mono text-xs text-right" style={{ color: 'var(--color-text-secondary)' }}>
                      {row.message}
                    </span>
                    <StatusPill status={pillStatus} label={pillLabel} />
                    {needsWork && (
                      <button
                        type="button"
                        onClick={() => navigate(getResolveDestination(row))}
                        className="text-xs underline-offset-2 hover:underline transition-colors whitespace-nowrap"
                        style={{ color: 'var(--color-text-secondary)' }}
                        data-testid={`signoff-resolve-${row.id}`}
                      >
                        Resolve →
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Post-failure blockers display (server-authoritative) */}
      {postFailureBlockers && postFailureBlockers.length > 0 && (
        <div className="mb-5 py-3 border-t border-b" style={{ borderColor: 'var(--color-border)' }}>
          <div className="text-xs font-medium mb-2" style={{ color: 'var(--color-blocker)' }}>
            Sign-off failed — resolve the following:
          </div>
          <ul className="space-y-1">
            {postFailureBlockers.map((blocker, i) => (
              <li key={i} className="text-xs flex items-start gap-2" style={{ color: 'var(--color-text-secondary)' }}>
                <span style={{ color: 'var(--color-blocker)' }}>•</span>
                {blocker}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Footer */}
      <div
        className="pt-5 flex items-center justify-end gap-3"
        style={{ borderTop: '1px solid var(--color-border)' }}
      >
        {hasMissingDocs && (
          <a
            href={`/wizard-v2?claim=${caseId}&step=3&section=upload`}
            className="inline-flex items-center px-5 h-10 rounded-[2px] text-[13px] font-medium transition-colors hover:opacity-80"
            style={{
              backgroundColor: 'transparent',
              border: '1px solid var(--color-border)',
              color: 'var(--color-text-primary)'
            }}
          >
            Upload missing documents
          </a>
        )}

        <button
          type="button"
          disabled={!effectiveReady || submitting}
          onClick={handleSignOffClick}
          onBlur={() => {
            if (!submitting) setArmed(false);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setArmed(false);
          }}
          className="inline-flex items-center justify-center px-5 h-10 rounded-[2px] text-[13px] font-semibold transition-colors focus-visible:ring-1 focus-visible:ring-brand focus-visible:ring-inset focus:outline-none"
          style={{
            backgroundColor: effectiveReady ? 'var(--color-brand)' : 'transparent',
            color: effectiveReady ? 'var(--color-on-brand)' : 'var(--color-text-primary)',
            border: effectiveReady ? 'none' : '1px solid var(--color-border)',
            opacity: effectiveReady ? 1 : 0.4,
            cursor: effectiveReady ? (submitting ? 'not-allowed' : 'pointer') : 'not-allowed',
          }}
          onMouseEnter={(e) => {
            if (effectiveReady && !submitting) {
              e.currentTarget.style.backgroundColor = 'var(--color-brand-hover, var(--color-brand))';
            }
          }}
          onMouseLeave={(e) => {
            if (effectiveReady && !submitting) {
              e.currentTarget.style.backgroundColor = 'var(--color-brand)';
            }
          }}
        >
          {submitting ? 'Signing off…' : armed ? 'Confirm sign off' : 'Sign off case'}
        </button>
      </div>

      {/* Generic post error (when no structured blockers returned) */}
      {postError && postFailureBlockers === null && (
        <div className="mt-3 font-mono text-xs text-right" style={{ color: 'var(--color-blocker)' }}>
          {postError}
        </div>
      )}
    </div>
  );
}
